import React, { useEffect, useMemo, useState } from 'react';
import { Box, Container, makeStyles, Typography, useTheme, useMediaQuery } from '@material-ui/core';
import { useLocation, useNavigate } from 'react-router-dom';
import Page from 'src/app/components/Page';
import { useDispatch, useSelector } from 'react-redux';
import {
  GetImages,
  AdminGetImages,
  resetListImg,
  GetImagesByUserId
} from '../../../../features/imageSlice';
import { PAGE_SIZE_IMAGE } from 'src/app/constant/config';
import LoadingComponent from '../../../components/Loading';
import ImageDetail from './ImageDetail';
import HeaderToolBar from './HeaderToolBar';
import Result from './Result';
import { _convertObjectToQuery } from 'src/app/utils/apiService';
import moment from 'moment';
import { getVehicleLicensePlateCamera } from 'src/features/vehicleSlice';
import PlaybackTab from './PlaybackTab';
import { setVehicleSelected } from 'src/features/playback';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: 0,
    paddingLeft: props => (props.isSidebarOpen ? '384px' : '28px'),
    transition: 'padding-left 0.3s ease'
  },
  productCard: {
    height: '100%'
  },
  errorMessage: {
    fontSize: '16px',
    fontWeight: 500,
    color: '#D9042F',
    textAlign: 'center',
    justifyContent: 'cemter'
  },
  loadingWrapper: {
    position: 'fixed',
    top: 0,
    left: props => (props.isSidebarOpen ? '384px' : '0'),
    width: props => (props.isSidebarOpen ? 'calc(100vw - 384px)' : '100vw'),
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }
}));

const ImageList = () => {
  const query = useQuery();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const start_date = query.get('start_date');
  const end_date = query.get('end_date');
  const serial = query.get('serial');
  const camera = query.get('camera');
  const licensePlate = query.get('license_plate');
  const mediaTab = query.get('tab');

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const classes = useStyles({ isSidebarOpen });
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const agencyId = useSelector(state => state.authSlice.agencyId);
  const dataLogin = useSelector(state => state.authSlice.dataLogin);
  const loginId = dataLogin?.id;

  const isLoading = useSelector(state => state.imageSlice.isLoading);
  const [isShowModalImgDetails, setShowModalImg] = React.useState(false);
  const [isShowResult, setShowResult] = React.useState(false);
  const [sendData, setSendData] = React.useState(undefined);

  const isAdmin =  useSelector(state => state.authSlice.isAdmin);
  const isAgency = useSelector(state => state.authSlice.isAgency);
  const isUser =  useSelector(state => state.authSlice.isUser);

  const getTabIndexFromQuery = tabValue => (tabValue === 'video' ? 1 : 0);
  const getTabQueryValue = tabIndex => (tabIndex === 1 ? 'video' : 'image');

  const [currentTab, setCurrentTab] = useState(getTabIndexFromQuery(mediaTab));

  const isVodLoading = useSelector(state => state.vodSlice?.isLoading);

  useEffect(() => {
    if (isAgency) {
      dispatch(getVehicleLicensePlateCamera(`/${agencyId}`));
    } else {
      dispatch(getVehicleLicensePlateCamera(''));
    }
  }, [isAdmin, isAgency, isUser, dispatch, agencyId]);

  useEffect(() => {
    return () => {
      dispatch(resetListImg());
    };
  }, [dispatch]);

  const [params, setParams] = useState({
    license_plate: licensePlate || '',
    serial: serial || '',
    start_date:
      start_date ||
      moment()
        .startOf('day')
        .unix(),
    end_date:
      end_date ||
      moment()
        .endOf('day')
        .unix(),
    camera: camera || ''
  });

  useEffect(() => {
    setCurrentTab(getTabIndexFromQuery(mediaTab));
  }, [mediaTab]);

  useEffect(() => {
    setParams({
      license_plate: licensePlate || '',
      serial: serial || '',
      start_date:
        start_date ||
        moment()
          .startOf('day')
          .unix(),
      end_date:
        end_date ||
        moment()
          .endOf('day')
          .unix(),
      camera: camera || ''
    });
  }, [licensePlate, serial, start_date, end_date, camera]);

  const getListImageWithParams = async data => {
    const { license_plate, ...restData } = data;
    const formattedData = {
      license_plate: license_plate || '',
      ...restData,
      start_date: moment(data.start_date).unix(),
      end_date: moment(data.end_date).unix()
    };
    const paramValue = Object.assign({}, params, formattedData);
    paramValue.tab = getTabQueryValue(currentTab);

    setParams(paramValue);
    navigate(`/app/camera?${_convertObjectToQuery(paramValue)}`);

    try {
      if (isAdmin) {
        await dispatch(AdminGetImages({ ...paramValue, tenant: paramValue.tenant || loginId })).unwrap();
      } else if (isAgency) {
        await dispatch(
          GetImages({ payload: { ...paramValue, tenant: loginId } })
        ).unwrap();
      } else if (isUser) {
        await dispatch(
          GetImagesByUserId({ id: loginId, data: paramValue })
        ).unwrap();
      }
      if (isMobile) {
        setIsSidebarOpen(false);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSearch = async data => {
    if (currentTab === 0) {
      await getListImageWithParams(data);
    } else {
      await handleSearchVideo(data);
    }
  };

  const handleSearchVideo = async data => {
    const { serial, camera, start_date, end_date, license_plate } = data;
    const startTimeZone = moment(start_date).unix();
    const endTimeZone = moment(end_date).unix();

    let camNumber = camera || '1';
    if (camNumber.toString().length === 3 && camNumber.toString().endsWith('01')) {
      camNumber = camNumber.toString().charAt(0);
    }
    const channelId = `${serial}_${camNumber.padStart(2, '0')}`;
    const paramValue = {
      ...params,
      license_plate: license_plate || '',
      serial,
      start_date: startTimeZone,
      end_date: endTimeZone,
      camera,
      tab: 'video'
    };

    setParams(paramValue);
    navigate(`/app/camera?${_convertObjectToQuery(paramValue)}`);

    const videoData = {
      first_time: startTimeZone,
      last_time: endTimeZone,
      deviceSerial: serial,
      channelId: channelId,
      channelName: `CH${camNumber.padStart(2, '0')}`,
    };

    // NOTE: this used to also dispatch(getListVod(...)) -- a legacy
    // src/features/playback.js thunk that hits https://cdn01.vgps.vn directly
    // (not proxied, so it CORS-fails from a browser origin) and populates
    // vodSlice.listVod/totalVod, which nothing reads anymore: the card grid
    // now comes from useRecordingList (PlaybackTab.js), fed by the SDK
    // runtime's listRecordings over the existing signaling socket. Removed
    // 2026-09-12 -- it was pure noise (a guaranteed-failing request on every
    // video search), not a dependency of the new player.
    dispatch(setVehicleSelected(videoData));
    setShowResult(true);
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };


  const clearSearch = () => {
    const paramValue = {
      license_plate: '',
      serial: '',
      start_date: moment().startOf('day').unix(),
      end_date: moment().endOf('day').unix(),
      camera: '',
      tab: getTabQueryValue(currentTab),
      page: 1,
      page_size: PAGE_SIZE_IMAGE
    };
    setParams(paramValue);
    navigate(`/app/camera?${_convertObjectToQuery(paramValue)}`);
    if (isAdmin) {
      dispatch(AdminGetImages({ ...paramValue, tenant: loginId }));
    } else {
      dispatch(GetImages({ payload: { ...paramValue, tenant: loginId } }));
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Keyed on the URL params only. Built inline this object got a fresh identity
  // (new moment instances) on every render of this component, so HeaderToolBar's
  // sync effect re-fired each time and overwrote whatever the user was typing.
  const querySearch = useMemo(
    () => ({
      license_plate: licensePlate || '',
      serial: serial,
      start_date: start_date ? moment.unix(start_date) : moment().startOf('day'),
      end_date: end_date ? moment.unix(end_date) : moment().endOf('day'),
      camera: camera
    }),
    [licensePlate, serial, start_date, end_date, camera]
  );

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setShowResult(false);
    dispatch(setVehicleSelected(null));

    const nextQuery = {
      ...params,
      tab: getTabQueryValue(newValue)
    };

    navigate(`/app/camera?${_convertObjectToQuery(nextQuery)}`);
  };

  return (
    <Page className={classes.root}>
      <HeaderToolBar
        querySearch={querySearch}
        isLoading={currentTab === 0 ? isLoading : isVodLoading}
        searchRef={handleSearch}
        clearSearchRef={clearSearch}
        setShowResult={setShowResult}
        toggleSidebar={toggleSidebar}
        isSidebarOpen={isSidebarOpen}
        currentTab={currentTab}
        onTabChange={handleTabChange}
      />
      <Container maxWidth={false} style={{ padding: 0 }}>
        <Box style={{ height: 'calc(100vh - 66px)', position: 'relative' }}>
          {currentTab === 0 ? (
            <>
              {isLoading ? (
                <div className={classes.loadingWrapper}>
                  <LoadingComponent />
                </div>
              ) : (
                <Result isShowResult={isShowResult} isSidebarOpen={isSidebarOpen} />
              )}
            </>
          ) : (
            <>
              <PlaybackTab isSidebarOpen={isSidebarOpen} isShowResult={isShowResult} />
            </>
          )}

          {isShowResult === false && currentTab === 0 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="60vh"
            >
              <Typography className={classes.errorMessage}>
                Vui lòng chọn biển số hoặc tên để tìm kiếm {currentTab === 0 ? 'ảnh' : 'video'}
              </Typography>
            </Box>
          )}
        </Box>
      </Container>
      {isShowModalImgDetails && sendData && (
        <ImageDetail
          open={isShowModalImgDetails}
          data={sendData}
          closeRef={() => setShowModalImg(false)}
        />
      )}
    </Page>
  );
};

export default ImageList;
